// Navbar.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import Swal, { getApparifyLogoutDialogOptions } from "../utils/apparifySwal";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ChevronDown, User, ShoppingCart, Search } from "lucide-react";

import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useUI } from "../context/UIContext";

import NotificationBell from "./NotificationBell";
import CartSidebar from "./CartSidebar";
import ThemeToggle from "./ThemeToggle";
import useDebounce from "../hooks/useDebounce";
import ProductSearchModal from "./search/ProductSearchModal";
import { searchSuggestions } from "../services/api";
import { buildLoginRedirect, PURCHASE_REASON } from "../utils/authRedirect";
import { SHOPPER_NAV_LINKS } from "../content/siteContent";

export default function Navbar({
  showSearch = false,
  showNotifications = true,
  showProfile = true,
  showCart = true,
  searchConfig = null,
}) {
  const {
    cartSidebarOpen,
    setCartSidebarOpen,
  } = useUI();

  const [profileOpen, setProfileOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);
  const [categoriesMenuOpen, setCategoriesMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const profileAnchorRef = useRef(null);
  const profilePanelRef = useRef(null);
  const shopMenuRef = useRef(null);
  const categoriesMenuRef = useRef(null);

  const { cartCount } = useCart();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [searchParams, setSearchParams] = useSearchParams();

  const isAuthed = Boolean(auth?.isAuthenticated);

  const searchParamKey = searchConfig?.searchParam || "search";
  const currentSearchValue = searchParams.get(searchParamKey) || "";
  const isUrlSyncedSearch = showSearch && Boolean(searchConfig?.liveSync);
  const suggestionsEnabled = showSearch && Boolean(searchConfig?.suggestionsEnabled);
  const searchSubmitPath = searchConfig?.submitPath || "/";
  const searchPlaceholder = searchConfig?.placeholder || "Search products...";
  const searchAriaLabel = searchConfig?.ariaLabel || "Search products";

  const [searchInput, setSearchInput] = useState(isUrlSyncedSearch ? currentSearchValue : "");
  const debouncedSearch = useDebounce(searchInput, 400);
  const debouncedSuggestionQuery = useDebounce(searchInput, 300);

  const searchInputRef = useRef(null);

  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const syncSearchParam = useCallback(
    (nextValue) => {
      const params = new URLSearchParams(searchParams);
      const normalizedValue = String(nextValue || "").trim();

      if (normalizedValue) {
        params.set(searchParamKey, normalizedValue);
      } else {
        params.delete(searchParamKey);
      }

      setSearchParams(params, { replace: true });
    },
    [searchParamKey, searchParams, setSearchParams]
  );

  const navigateToSearchDestination = useCallback(
    (nextValue) => {
      const normalizedValue = String(nextValue || "").trim();
      const params = new URLSearchParams();

      if (normalizedValue) {
        params.set(searchParamKey, normalizedValue);
      }

      const nextQuery = params.toString();
      navigate(nextQuery ? `${searchSubmitPath}?${nextQuery}` : searchSubmitPath);
    },
    [navigate, searchParamKey, searchSubmitPath]
  );

  useEffect(() => {
    if (isUrlSyncedSearch) {
      setSearchInput(currentSearchValue);
    } else {
      setSearchInput("");
    }
  }, [currentSearchValue, isUrlSyncedSearch, pathname]);

  useEffect(() => {
    if (!isUrlSyncedSearch) return;

    const current = currentSearchValue;
    const target = debouncedSearch.trim();
    if (target === current) return;

    syncSearchParam(target);
  }, [currentSearchValue, debouncedSearch, isUrlSyncedSearch, syncSearchParam]);

  useEffect(() => {
    const q = debouncedSuggestionQuery.trim();

    if (!searchOpen || !suggestionsEnabled) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    setSuggestionsLoading(true);

    searchSuggestions(q, 8)
      .then((data) => {
        if (cancelled) return;
        setSuggestions(Array.isArray(data) ? data : []);
        setHighlightedIndex(-1);
      })
      .catch(() => {
        if (cancelled) return;
        setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSuggestionQuery, searchOpen, suggestionsEnabled]);

  useEffect(() => {
    setShowDropdown(false);
    setSuggestions([]);
    setSearchOpen(false);
    setShopMenuOpen(false);
    setCategoriesMenuOpen(false);
  }, [pathname]);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setHighlightedIndex(-1);
  }, []);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSuggestionSelect = useCallback(
    (item) => {
      closeDropdown();
      setSuggestions([]);
      setSearchInput(item.name);
      setSearchOpen(false);
      navigate(`/product/${item.id}`);
    },
    [closeDropdown, navigate]
  );

  const submitSearch = useCallback(() => {
    closeDropdown();

    const q = searchInput.trim();
    if (!q) return;

    setSearchOpen(false);

    if (isUrlSyncedSearch) {
      syncSearchParam(q);
      return;
    }

    navigateToSearchDestination(q);
  }, [closeDropdown, isUrlSyncedSearch, navigateToSearchDestination, searchInput, syncSearchParam]);

  const viewAllSearchResults = useCallback(() => {
    const q = searchInput.trim();
    if (!q) return;

    const params = new URLSearchParams();
    params.set(searchParamKey, q);
    setSearchOpen(false);
    closeDropdown();
    navigate(`/products?${params.toString()}`);
  }, [closeDropdown, navigate, searchInput, searchParamKey]);

  const handleSearchKeyDown = (e) => {
    if (suggestionsEnabled && showDropdown && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, -1));
        return;
      }

      if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        handleSuggestionSelect(suggestions[highlightedIndex]);
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }

    if (e.key === "Escape") {
      if (showDropdown) {
        closeDropdown();
        return;
      }

      setSearchInput("");

      if (isUrlSyncedSearch) {
        syncSearchParam("");
      }

      searchInputRef.current?.blur();
    }
  };

  const isSearchPending = isUrlSyncedSearch ? searchInput !== debouncedSearch : suggestionsLoading;
  const searchInputA11yProps = suggestionsEnabled
    ? {
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-controls": "search-suggestions-listbox",
        "aria-haspopup": "listbox",
        "aria-expanded": showDropdown && suggestionsEnabled,
      }
    : {
        role: "searchbox",
      };

  useEffect(() => {
    const onClick = (e) => {
      const target = e.target;
      const clickedAnchor = profileAnchorRef.current?.contains(target);
      const clickedPanel = profilePanelRef.current?.contains(target);
      const clickedShopMenu = shopMenuRef.current?.contains(target);
      const clickedCategoriesMenu = categoriesMenuRef.current?.contains(target);
      if (!clickedAnchor && !clickedPanel) setProfileOpen(false);
      if (!clickedShopMenu) setShopMenuOpen(false);
      if (!clickedCategoriesMenu) setCategoriesMenuOpen(false);
    };

    const onEsc = (e) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
        setShopMenuOpen(false);
        setCategoriesMenuOpen(false);
        setSearchOpen(false);
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [closeDropdown, profileOpen]);

  const handleLogout = async () => {
    setProfileOpen(false);

    const res = await Swal.fire({
      ...getApparifyLogoutDialogOptions("shopper"),
    });

    if (!res.isConfirmed) return;

    try {
      await auth.logout?.();

      await Swal.fire({
        icon: "success",
        title: "Signed out",
        html: "See you again!",
        timer: 900,
        showConfirmButton: false,
      });

      navigate("/", { replace: true });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Logout failed",
        html: e?.message || "Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const goProfileSettings = () => {
    setProfileOpen(false);
    navigate("/profile/edit");
  };

  const toggleSearch = () => {
    if (searchOpen) {
      setSearchOpen(false);
      closeDropdown();
      return;
    }

    if (suggestionsEnabled) {
      setSuggestionsLoading(true);
    }
    setShowDropdown(suggestionsEnabled);
    setSearchOpen(true);
  };

  const closeSearchModal = useCallback(() => {
    setSearchOpen(false);
    closeDropdown();
  }, [closeDropdown]);

  const isShopLinkActive = (to) => {
    const [targetPath, targetHash] = String(to).split("#");
    if (targetHash) {
      return pathname === targetPath && location.hash === `#${targetHash}`;
    }
    if (to === "/") return pathname === "/" && !location.hash;
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <>
      <nav
        className="sticky top-0 z-50 w-full min-h-[56px] border-b border-neutral-200 bg-white/90 px-3 py-2.5 shadow-sm backdrop-blur
                   dark:border-neutral-800 dark:bg-neutral-900/90 dark:shadow-none
                   sm:min-h-[64px] sm:px-4 sm:py-3"
      >
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
          {/* LEFT - SHOP NAVIGATION */}
          <div className="min-w-0 justify-self-start">
            <div className="hidden items-center gap-4 lg:flex xl:gap-6" aria-label="Shop navigation">
              {SHOPPER_NAV_LINKS.map((link) => {
                const hasChildren = Array.isArray(link.children) && link.children.length > 0;
                const active = isShopLinkActive(link.to)
                  || link.children?.some((child) => isShopLinkActive(child.to));

                if (hasChildren) {
                  return (
                    <div key={link.to} ref={categoriesMenuRef} className="relative">
                      <button
                        type="button"
                        onClick={() => setCategoriesMenuOpen((open) => !open)}
                        className={[
                          "relative inline-flex items-center gap-1 whitespace-nowrap py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 transition hover:text-neutral-950 dark:text-neutral-400 dark:hover:text-white xl:text-[11px]",
                          "after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-left after:bg-current after:transition-transform",
                          active ? "text-neutral-950 after:scale-x-100 dark:text-white" : "after:scale-x-0 hover:after:scale-x-100",
                        ].join(" ")}
                        aria-haspopup="menu"
                        aria-expanded={categoriesMenuOpen}
                      >
                        {link.label}
                        <ChevronDown size={13} className={`transition ${categoriesMenuOpen ? "rotate-180" : ""}`} />
                      </button>

                      {categoriesMenuOpen ? (
                        <div role="menu" className="theme-menu-panel absolute left-0 z-[100] mt-2 w-52">
                          <Link
                            to={link.to}
                            onClick={() => setCategoriesMenuOpen(false)}
                            role="menuitem"
                            className="theme-menu-item rounded-none px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em]"
                          >
                            Shop by Category
                          </Link>
                          {link.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setCategoriesMenuOpen(false)}
                              role="menuitem"
                              className={[
                                "theme-menu-item rounded-none border-t px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] theme-menu-divider",
                                isShopLinkActive(child.to) ? "theme-menu-item-active" : "",
                              ].join(" ")}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={[
                      "relative whitespace-nowrap py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 transition hover:text-neutral-950 dark:text-neutral-400 dark:hover:text-white xl:text-[11px]",
                      "after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-left after:bg-current after:transition-transform",
                      active ? "text-neutral-950 after:scale-x-100 dark:text-white" : "after:scale-x-0 hover:after:scale-x-100",
                    ].join(" ")}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            <div ref={shopMenuRef} className="relative lg:hidden">
              <button
                type="button"
                onClick={() => setShopMenuOpen((open) => !open)}
                className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-700 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                aria-haspopup="menu"
                aria-expanded={shopMenuOpen}
              >
                Shop
                <ChevronDown size={14} className={`transition ${shopMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {shopMenuOpen ? (
                <div role="menu" className="theme-menu-panel absolute left-0 z-[100] mt-2 w-52">
                  {SHOPPER_NAV_LINKS.map((link) => (
                    <div key={link.to} role="none">
                      <Link
                        to={link.to}
                        onClick={() => setShopMenuOpen(false)}
                        role="menuitem"
                        className={[
                          "theme-menu-item rounded-none px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em]",
                          isShopLinkActive(link.to) ? "theme-menu-item-active" : "",
                        ].join(" ")}
                      >
                        {link.label}
                      </Link>
                      {link.children?.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => setShopMenuOpen(false)}
                          role="menuitem"
                          className="theme-menu-item theme-menu-item-subtle rounded-none border-l-2 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] theme-menu-divider"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* CENTER - BRAND */}
          <div className="flex min-h-[40px] shrink-0 items-center justify-self-center px-1 py-1">
            <span
              role="img"
              aria-label="Apparify"
              className="block h-7 w-24 bg-neutral-950 dark:bg-neutral-50 sm:w-36 lg:w-44"
              style={{
                WebkitMaskImage: "url(/images/apparify-wordmark.png)",
                maskImage: "url(/images/apparify-wordmark.png)",
                WebkitMaskMode: "luminance",
                maskMode: "luminance",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "100% auto",
                maskSize: "100% auto",
              }}
            />
          </div>

          {/* RIGHT - SHOPPER ACTIONS */}
          <div className="flex min-w-0 shrink-0 items-center justify-self-end gap-0.5 text-neutral-800 dark:text-neutral-50 sm:gap-1 md:gap-2">
            {showSearch ? (
              <button
                type="button"
                onClick={toggleSearch}
                className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg p-2 transition hover:bg-neutral-100 active:scale-[0.97] dark:hover:bg-neutral-800"
                aria-label={searchOpen ? "Close product search" : "Open product search"}
                aria-haspopup="dialog"
                aria-expanded={searchOpen}
              >
                <Search size={20} />
              </button>
            ) : null}

            {showNotifications ? <NotificationBell /> : null}

            {showProfile ? (
              <div className="relative" ref={profileAnchorRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen((prev) => !prev)}
                  className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg p-2 transition hover:bg-neutral-100 active:scale-[0.97]
                             dark:hover:bg-neutral-800"
                  aria-label={isAuthed ? "Profile options" : "Account options"}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                >
                  <User size={20} />
                </button>

                {profileOpen && (
                  <div
                    ref={profilePanelRef}
                    role="menu"
                    className="theme-menu-panel absolute right-0 z-[100] mt-2 w-56 sm:w-64"
                  >
                    {isAuthed ? (
                      <>
                      <button
                        type="button"
                        onClick={goProfileSettings}
                        role="menuitem"
                        className="theme-menu-item rounded-none px-4 py-3 text-xs font-semibold tracking-[0.25em]"
                      >
                        PROFILE
                      </button>

                      <div className="border-t theme-menu-divider" />

                      <button
                        type="button"
                        onClick={() => {
                          setProfileOpen(false);
                          navigate("/business/register", {
                            state: {
                              from: `${location.pathname}${location.search}${location.hash}`,
                            },
                          });
                        }}
                        role="menuitem"
                        className="theme-menu-item rounded-none px-4 py-3 text-xs font-semibold tracking-[0.25em]"
                      >
                        APPLY FOR BUSINESS
                      </button>

                      <div className="border-t theme-menu-divider" />

                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <span className="theme-menu-label tracking-[0.25em]">
                          THEME
                        </span>
                        <ThemeToggle />
                      </div>

                      <div className="border-t theme-menu-divider" />

                      <button
                        type="button"
                        onClick={handleLogout}
                        role="menuitem"
                        className="theme-menu-item theme-menu-item-danger rounded-none px-4 py-3 text-xs font-semibold tracking-[0.25em]"
                      >
                        LOGOUT
                      </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setProfileOpen(false);
                            navigate("/login");
                          }}
                          role="menuitem"
                          className="theme-menu-item rounded-none px-4 py-3 text-xs font-semibold tracking-[0.25em]"
                        >
                          LOGIN
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setProfileOpen(false);
                            navigate("/register");
                          }}
                          role="menuitem"
                          className="theme-menu-item rounded-none px-4 py-3 text-xs font-semibold tracking-[0.25em]"
                        >
                          REGISTER
                        </button>

                        <div className="border-t theme-menu-divider" />

                        <div className="flex items-center justify-between gap-4 px-4 py-3">
                          <span className="theme-menu-label tracking-[0.25em]">
                            THEME
                          </span>
                          <ThemeToggle />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {showCart ? (
              <button
                onClick={() => {
                  if (!isAuthed) {
                    navigate(buildLoginRedirect("/cart", PURCHASE_REASON));
                    return;
                  }
                  setCartSidebarOpen(true);
                }}
                className="relative hidden min-h-[40px] min-w-[40px] items-center justify-center rounded-lg p-2 transition hover:bg-neutral-100 active:scale-[0.97] md:flex
                           dark:hover:bg-neutral-800"
                aria-label={`Shopping cart with ${cartCount} items`}
              >
                <ShoppingCart size={20} />

                {cartCount > 0 && (
                  <span
                    className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[8px] font-bold text-white shadow-sm ring-2 ring-white
                               dark:ring-neutral-900 sm:text-[9px]"
                  >
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </button>
            ) : null}
          </div>
        </div>
      </nav>

      <ProductSearchModal
        open={showSearch && searchOpen}
        inputRef={searchInputRef}
        query={searchInput}
        onQueryChange={(value) => {
          setSearchInput(value);
          setShowDropdown(suggestionsEnabled);
        }}
        onKeyDown={handleSearchKeyDown}
        onSubmit={submitSearch}
        onViewAll={viewAllSearchResults}
        onClose={closeSearchModal}
        products={suggestions}
        loading={suggestionsLoading}
        pending={isSearchPending}
        highlightedIndex={highlightedIndex}
        onHighlight={setHighlightedIndex}
        onSelect={handleSuggestionSelect}
        inputAriaLabel={searchAriaLabel}
        inputPlaceholder={searchPlaceholder}
        inputA11yProps={searchInputA11yProps}
      />

      <CartSidebar isOpen={cartSidebarOpen} onClose={() => setCartSidebarOpen(false)} />
    </>
  );
}
